import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { matchesAll } from "@/lib/trFold";
import { formatVariantLabel, sortVariants } from "@/lib/variants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, FileText, Loader2, Search, ShoppingBag, X } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["adet", "metre", "kg", "gram", "kutu", "paket", "rulo", "çift"];
const CURRENCIES = ["TRY", "USD", "EUR", "GBP"];
const TAX_RATES = [0, 1, 8, 10, 18, 20];
const CUR_SYMBOL = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };

// TR sayı parse: virgül = ondalık, nokta + 3 hane = binlik (PurchaseQuoteDialog mantığı)
const parseTr = (v) => {
  if (v == null) return NaN;
  let s = String(v).trim();
  if (!s) return NaN;
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};
const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DirectPurchaseDialog({ open, onOpenChange, onSuccess, onQuoteRequest, initialProduct = null }) {
  const { can } = useAuth();
  const [mode, setMode] = useState(null); // null | "direct" | "quote"
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [supplierQuery, setSupplierQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [variantId, setVariantId] = useState("");
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("adet");
  const [warehouseId, setWarehouseId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [currency, setCurrency] = useState("TRY");
  const [taxRate, setTaxRate] = useState("20");
  const [termin, setTermin] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(null);
    setProduct(null); setVariants([]); setVariantId(""); setProductQuery(""); setResults([]);
    setQty(""); setUnit("adet"); setWarehouseId(""); setSupplierId(""); setSupplierQuery("");
    setUnitPrice(""); setCurrency("TRY"); setTaxRate("20"); setTermin(""); setNote("");
    // İhtiyaç ekranından gelen hazır ürün ön-doldurması (type: "product")
    if (initialProduct?.id) {
      setMode("direct");
      pickProduct({
        product: { id: initialProduct.id, name: initialProduct.name || "", sku: initialProduct.code || "", code: initialProduct.code || "" },
        variant: initialProduct.variant_id ? { id: initialProduct.variant_id } : null,
      });
    }
    api.get("/suppliers").then(({ data }) => setSuppliers(data || [])).catch(() => {});
    api.get("/warehouses/summary").then(({ data }) => {
      const active = (data || []).filter((w) => w.is_active !== false);
      setWarehouses(active);
      if (active.length === 1) setWarehouseId(active[0].id);
    }).catch(() => {});
  }, [open]);

  // Ürün arama — 300ms debounce, mevcut /products/search (TR matchesAll) reuse
  useEffect(() => {
    if (!open || product) return undefined;
    const q = productQuery.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/products/search", { params: { q, limit: 10 } });
        setResults(Array.isArray(data) ? data : []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [productQuery, open, product]);

  const pickProduct = async (pair) => {
    const p = pair.product || pair;
    setProduct(p);
    setResults([]);
    setProductQuery("");
    setVariantId("");
    try {
      const { data } = await api.get(`/products/${p.id}/variants`);
      const sorted = sortVariants(data || []);
      setVariants(sorted);
      if (pair.variant?.id) setVariantId(pair.variant.id);
    } catch { setVariants([]); }
  };

  const filteredSuppliers = useMemo(() => {
    if (!supplierQuery.trim()) return suppliers;
    return suppliers.filter((s) => matchesAll(supplierQuery, [s.name, s.contact_person, s.phone, s.email]));
  }, [suppliers, supplierQuery]);

  const q = parseTr(qty);
  const up = parseTr(unitPrice);
  const tax = Number(taxRate || 0);
  const subtotal = Number.isFinite(q) && Number.isFinite(up) ? q * up : 0;
  const taxTotal = subtotal * (tax / 100);
  const grandTotal = subtotal + taxTotal;
  const canSubmit = !!product && Number.isFinite(q) && q > 0 && Number.isFinite(up) && up >= 0 && !!supplierId && !saving;
  const canQuote = !!product && Number.isFinite(q) && q > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const selVar = variants.find((v) => v.id === variantId);
      const { data: res } = await api.post("/purchase-orders/bulk-create", {
        lines: [{
          kind: "product",
          supplier_id: supplierId,
          product_id: product.id,
          variant_id: variantId || "",
          variant_sku: selVar?.sku || "",
          product_name: product.name || "",
          brand_name: product.brand || "",
          quantity: q,
          unit,
          unit_price: up,
          currency,
          tax_rate: tax,
        }],
        order_notes: note,
        expected_delivery_date: termin ? new Date(termin).toISOString() : null,
        target_warehouse_id: warehouseId || "",
      });
      const po = (res.created || [])[0] || {};
      toast.success(`Satın alma siparişi oluşturuldu: ${po.po_no || ""} — Mal Kabul'e düştü`);
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Teklif Topla — mevcut teklif karşılaştırma altyapısına (PurchaseQuoteDialog) devreder
  const startQuote = () => {
    if (!canQuote) return;
    const selVar = variants.find((v) => v.id === variantId);
    onOpenChange(false);
    onQuoteRequest?.({
      material_id: product.id,
      variant_id: variantId || "",
      kind: "hazir_urun",
      name: product.name || "",
      code: selVar?.sku || product.sku || product.code || "",
      unit,
      total_shortage: q,
      manual: true,
    });
  };

  const productPicker = (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ürün *</Label>
      {product ? (
        <div className="flex items-center justify-between gap-2 rounded-sm border border-slate-700 bg-slate-950 px-3 py-2" data-testid="dp-selected-product">
          <div className="min-w-0">
            <div className="text-sm text-slate-100 font-semibold truncate">{product.name}</div>
            <div className="text-[10px] font-mono text-slate-500 truncate">{product.sku || product.code || product.id}</div>
          </div>
          <Button variant="ghost" size="sm" data-testid="dp-clear-product" onClick={() => { setProduct(null); setVariants([]); setVariantId(""); }} className="h-7 w-7 p-0 text-slate-500 hover:text-red-400">
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="dp-product-search"
            value={productQuery}
            onChange={(e) => setProductQuery(e.target.value)}
            placeholder="Ürün adı, SKU veya barkod ara…"
            className="pl-9 bg-slate-950 border-slate-700 rounded-sm h-9"
          />
          {(searching || results.length > 0) && (
            <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-sm border border-slate-700 bg-slate-950 shadow-xl" data-testid="dp-product-results">
              {searching && <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Aranıyor…</div>}
              {results.map((r, i) => {
                const p = r.product || r;
                return (
                  <button
                    key={`${p.id}-${r.variant?.id || i}`}
                    type="button"
                    data-testid={`dp-product-result-${p.id}`}
                    onClick={() => pickProduct(r)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-800 border-b border-slate-900 last:border-0"
                  >
                    <div className="text-sm text-slate-100 truncate">{p.name}</div>
                    <div className="text-[10px] font-mono text-slate-500 truncate">
                      {p.sku || p.code || ""}{r.variant ? ` · ${formatVariantLabel(r.variant.attributes, r.variant.sku)}` : ""}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const variantPicker = (
    <div className="space-y-1.5 col-span-2">
      <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Varyant</Label>
      <Select value={variantId || "none"} onValueChange={(v) => setVariantId(v === "none" ? "" : v)} disabled={!product}>
        <SelectTrigger data-testid="dp-variant-select" className="bg-slate-950 border-slate-700 rounded-sm h-9">
          <SelectValue placeholder={product ? "Varyant seç" : "Önce ürün seçin"} />
        </SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
          <SelectItem value="none">Varyantsız / Genel</SelectItem>
          {variants.map((v) => (
            <SelectItem key={v.id} value={v.id}>{formatVariantLabel(v.attributes, v.sku)}{v.sku ? ` · ${v.sku}` : ""}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const qtyInput = (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Miktar *</Label>
      <Input data-testid="dp-qty" type="text" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="0" className="bg-slate-950 border-slate-700 rounded-sm h-9 font-mono" />
    </div>
  );

  const unitSelect = (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Birim</Label>
      <Select value={unit} onValueChange={setUnit}>
        <SelectTrigger data-testid="dp-unit-select" className="bg-slate-950 border-slate-700 rounded-sm h-9"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
          {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-2xl max-h-[92vh] flex flex-col overflow-hidden" data-testid="direct-purchase-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-slate-100 flex items-center gap-2">
            {mode && (
              <button type="button" data-testid="dp-mode-back" onClick={() => setMode(null)} className="text-slate-500 hover:text-slate-300 transition-colors">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            {mode === "direct" ? "Direkt Sipariş Ver" : mode === "quote" ? "Teklif Topla" : "Direkt Satın Alma"}
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            {mode === "direct"
              ? "İhtiyaç beklemeden manuel satın alma siparişi oluşturur. Sipariş doğrudan Mal Kabul akışına düşer."
              : mode === "quote"
                ? "Aynı ürün için birden fazla tedarikçiden teklif toplayıp karşılaştırma ekranında siparişe dönüştürürsünüz."
                : "Nasıl ilerlemek istersiniz?"}
          </DialogDescription>
        </DialogHeader>

        {/* Mod seçimi */}
        {!mode && (
          <div className="flex-1 min-h-0 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-3 py-3" data-testid="dp-mode-select">
            <button
              type="button"
              data-testid="dp-mode-direct"
              onClick={() => setMode("direct")}
              className="group rounded-sm border border-slate-700 bg-slate-950 p-5 text-left hover:border-emerald-600/60 hover:bg-emerald-950/20 transition-colors"
            >
              <ShoppingBag className="w-6 h-6 text-emerald-500 mb-3" />
              <div className="text-sm font-semibold text-slate-100">Direkt Sipariş Ver</div>
              <div className="text-xs text-slate-500 mt-1 leading-relaxed">Tedarikçi ve fiyat belli — sipariş hemen oluşur, Mal Kabul'e düşer.</div>
            </button>
            <button
              type="button"
              data-testid="dp-mode-quote"
              onClick={() => setMode("quote")}
              className="group rounded-sm border border-slate-700 bg-slate-950 p-5 text-left hover:border-sky-600/60 hover:bg-sky-950/20 transition-colors"
            >
              <FileText className="w-6 h-6 text-sky-500 mb-3" />
              <div className="text-sm font-semibold text-slate-100">Teklif Topla</div>
              <div className="text-xs text-slate-500 mt-1 leading-relaxed">Birden fazla tedarikçiden teklif topla, karşılaştır, seçimi siparişe dönüştür.</div>
            </button>
          </div>
        )}

        {/* Direkt Sipariş formu */}
        {mode === "direct" && (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-2 pr-1">
              {productPicker}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {variantPicker}
                {qtyInput}
                {unitSelect}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* Tedarikçi */}
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tedarikçi *</Label>
                  <Select value={supplierId || "none"} onValueChange={(v) => setSupplierId(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="dp-supplier-select" className="bg-slate-950 border-slate-700 rounded-sm h-9">
                      <SelectValue placeholder="Tedarikçi seç" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                      <div className="p-1.5 sticky top-0 bg-slate-900">
                        <Input data-testid="dp-supplier-search" value={supplierQuery} onChange={(e) => setSupplierQuery(e.target.value)} placeholder="Ara…" className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs" />
                      </div>
                      <SelectItem value="none" disabled>Seçin…</SelectItem>
                      {filteredSuppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {/* Hedef depo */}
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Hedef Depo</Label>
                  <Select value={warehouseId || "none"} onValueChange={(v) => setWarehouseId(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="dp-warehouse-select" className="bg-slate-950 border-slate-700 rounded-sm h-9">
                      <SelectValue placeholder="Mal Kabul'de seçilir" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                      <SelectItem value="none">Mal Kabul'de seçilir</SelectItem>
                      {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* Birim fiyat */}
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Birim Fiyat *</Label>
                  <Input data-testid="dp-unit-price" type="text" inputMode="decimal" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} placeholder="0,00" className="bg-slate-950 border-slate-700 rounded-sm h-9 font-mono" />
                </div>
                {/* Para birimi */}
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Para Birimi</Label>
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger data-testid="dp-currency-select" className="bg-slate-950 border-slate-700 rounded-sm h-9"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                      {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {/* KDV */}
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">KDV %</Label>
                  <Select value={taxRate} onValueChange={setTaxRate}>
                    <SelectTrigger data-testid="dp-tax-select" className="bg-slate-950 border-slate-700 rounded-sm h-9"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                      {TAX_RATES.map((t) => <SelectItem key={t} value={String(t)}>%{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {/* Termin */}
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Termin</Label>
                  <Input data-testid="dp-termin" type="date" value={termin} onChange={(e) => setTermin(e.target.value)} className="bg-slate-950 border-slate-700 rounded-sm h-9 font-mono" />
                </div>
              </div>
              {/* Not */}
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Sipariş Notu</Label>
                <Textarea data-testid="dp-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="bg-slate-950 border-slate-700 rounded-sm text-sm" />
              </div>
              {/* Toplam özeti */}
              <div className="flex items-center justify-end gap-5 rounded-sm border border-slate-800 bg-slate-950/60 px-4 py-2.5 font-mono text-xs" data-testid="dp-totals">
                <span className="text-slate-500">Ara Toplam <span className="text-slate-200 tabular-nums">{CUR_SYMBOL[currency]}{fmt(subtotal)}</span></span>
                <span className="text-slate-500">KDV <span className="text-slate-200 tabular-nums">{CUR_SYMBOL[currency]}{fmt(taxTotal)}</span></span>
                <span className="text-slate-400 font-semibold">Genel Toplam <span className="text-emerald-400 tabular-nums">{CUR_SYMBOL[currency]}{fmt(grandTotal)}</span></span>
              </div>
            </div>
            <DialogFooter className="shrink-0 border-t border-slate-800 pt-3">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm" data-testid="dp-cancel-btn">İptal</Button>
              <Button onClick={submit} disabled={(!canSubmit) || !(can("purchases", "add"))} title={!(can("purchases", "add")) ? "Satın alma siparişi oluşturma yetkiniz yok (purchases.add)" : undefined} className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" data-testid="dp-submit-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Satın Alma Siparişi Oluştur
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Teklif Topla formu */}
        {mode === "quote" && (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-2 pr-1">
              {productPicker}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {variantPicker}
                {qtyInput}
                {unitSelect}
              </div>
            </div>
            <DialogFooter className="shrink-0 border-t border-slate-800 pt-3">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm" data-testid="dp-quote-cancel-btn">İptal</Button>
              <Button onClick={startQuote} disabled={!canQuote} className="bg-sky-600 hover:bg-sky-500 rounded-sm" data-testid="dp-quote-start-btn">
                Teklif Toplamaya Başla
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
